import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { TableAction, TableColumn, TableComponent } from '../../common/table/table.component';
import { TenantUsersModalComponent, TenantUserModalMode } from '../../modals/tenant-users/tenant-users.component';
import { ManageUserServicesModalComponent } from '../../modals/manage-user-services/manage-user-services.component';
import { LoaderComponent } from '../../common/loader/loader.component';
import { TenantUsersService } from '../../services/tenant-users.service';
import { ToastrService } from '../../services/toastr.service';
import { TenantUser } from '../../models/tenant-user.model';

@Component({
  selector: 'app-tenant-users',
  imports: [TableComponent, TenantUsersModalComponent, ManageUserServicesModalComponent, LoaderComponent],
  templateUrl: './tenant-users.component.html',
  styleUrl: './tenant-users.component.scss'
})
export class TenantUsersComponent implements OnInit {
  tenantUid: string;

  actions: TableAction[] = ['edit', 'changePassword', 'delete'];

  private baseColumns: TableColumn[] = [
    { key: 'email', label: 'Email' },
    { key: 'firstName', label: 'First Name' },
    { key: 'lastName', label: 'Last Name' },
    { key: 'tenant', label: 'Tenant' }
  ];

  private servicesColumn: TableColumn = {
    key: 'services',
    label: 'Services',
    type: 'button',
    buttonIcon: 'bi-sliders',
    buttonLabel: 'Manage'
  };

  users: TenantUser[] = [];
  rows: any[] = [];
  searchTerm = '';

  page = 1;
  pageSize = 10;
  totalItems = 0;

  loading = true;

  modalOpen = false;
  modalMode: TenantUserModalMode = 'add';
  selectedUser: TenantUser | null = null;

  manageServicesOpen = false;
  manageServicesUser: TenantUser | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private tenantUsersService: TenantUsersService,
    private toastrService: ToastrService
  ) {
    this.tenantUid = this.route.snapshot.paramMap.get('slug') ?? '';
  }

  ngOnInit(): void {
    this.load();
  }

  private load(): void {
    this.loading = true;

    this.tenantUsersService.getUsers(this.tenantUid, this.page).subscribe({
      next: (response) => {
        this.users = response.results;
        this.totalItems = response.count;
        if (this.page === 1 && response.results.length > 0) {
          this.pageSize = response.results.length;
        }
        this.applyFilter();
        this.loading = false;
      },
      error: () => {
        this.loading = false;
        this.toastrService.error('Unable to load users.');
      }
    });
  }

  onSearch(term: string): void {
    this.searchTerm = term;
    this.applyFilter();
  }

  onPageChange(page: number): void {
    this.page = page;
    this.load();
  }

  private applyFilter(): void {
    const term = this.searchTerm.trim().toLowerCase();
    const filtered = term
      ? this.users.filter(u =>
          u.email.toLowerCase().includes(term) ||
          u.first_name.toLowerCase().includes(term) ||
          u.last_name.toLowerCase().includes(term)
        )
      : this.users;

    this.rows = filtered.map(u => ({
      email: u.email,
      firstName: u.first_name,
      lastName: u.last_name,
      tenant: u.tenant,
      __user: u
    }));
  }

  get isGlobal(): boolean {
    return !this.tenantUid;
  }

  // Services are assigned per tenant-service, so "Manage" only makes sense in a
  // tenant-scoped view — the global /users page has no tenant to scope the catalog to.
  get columns(): TableColumn[] {
    return this.isGlobal ? this.baseColumns : [...this.baseColumns, this.servicesColumn];
  }

  goBack(): void {
    if (this.isGlobal) {
      this.router.navigate(['/']);
      return;
    }
    this.router.navigate(['/tenants', this.tenantUid]);
  }

  openAdd(): void {
    this.selectedUser = null;
    this.modalMode = 'add';
    this.modalOpen = true;
  }

  onEdit(row: any): void {
    this.selectedUser = row.__user;
    this.modalMode = 'edit';
    this.modalOpen = true;
  }

  onChangePassword(row: any): void {
    this.selectedUser = row.__user;
    this.modalMode = 'changePassword';
    this.modalOpen = true;
  }

  onDelete(row: any): void {
    this.selectedUser = row.__user;
    this.modalMode = 'delete';
    this.modalOpen = true;
  }

  onColumnAction(event: { key: string; row: any }): void {
    if (event.key === 'services') {
      this.manageServicesUser = event.row.__user;
      this.manageServicesOpen = true;
    }
  }

  onManageServicesClosed(): void {
    this.manageServicesOpen = false;
    this.manageServicesUser = null;
  }

  onManageServicesSaved(): void {
    this.toastrService.success('Service assignments updated successfully.');
    this.load();
  }

  onModalClosed(): void {
    this.modalOpen = false;
    this.selectedUser = null;
  }

  onModalSaved(): void {
    const messages: Record<TenantUserModalMode, string> = {
      add: 'User added successfully.',
      edit: 'User updated successfully.',
      changePassword: 'Password updated successfully.',
      delete: 'User removed successfully.'
    };
    this.toastrService.success(messages[this.modalMode]);
    this.load();
  }
}
